import React, { useState, useCallback, useRef } from "react";
import { useDropzone } from "react-dropzone";
import { BsPersonAdd } from "react-icons/bs";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { postProfileImgUpload } from "../repo/ChangeProfileImg";
import { setReProfileImg } from "../../../../common/redux/action/ProfileAction";
import {
  createDefaultProfileFile,
  getUpdatedProfileImage,
} from "../util/ProfileImage";

export const useProfileImageUpload = () => {
  const dispatch = useDispatch();
  const [isUploading, setIsUploading] = useState(false);
  const uploadInProgress = useRef(false);

  const uploadImage = useCallback(
    async (getFile) => {
      if (uploadInProgress.current) return;
      uploadInProgress.current = true;
      setIsUploading(true);
      try {
        const file = await getFile();
        const response = await postProfileImgUpload(file);
        if (response.msg !== "Success") {
          throw new Error("프로필 사진 업로드에 실패했습니다.");
        }

        const imageUrl = await getUpdatedProfileImage(response.data.data);
        dispatch(setReProfileImg(imageUrl));
      } catch (error) {
        // 변경에 실패하면 기존 프로필 사진을 유지합니다.
        return;
      } finally {
        uploadInProgress.current = false;
        setIsUploading(false);
      }
    },
    [dispatch]
  );

  return {
    isUploading,
    uploadImage,
    resetProfileImage: () => uploadImage(createDefaultProfileFile),
  };
};

export const ProfileImageUpload = ({ profileImg, onUpload, isUploading }) => {
  const { t } = useTranslation();
  const [failedImage, setFailedImage] = useState(null);

  const onDrop = useCallback(
    (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) onUpload(() => file);
    },
    [onUpload]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
    multiple: false,
    disabled: isUploading,
  });

  return (
    <>
      <input
        {...getInputProps({
          disabled: isUploading,
          "aria-label": t("profileImageChangeText"),
        })}
      />
      <div
        className="profile"
        style={{
          height: "10rem",
          width: "10rem",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          overflow: "hidden",
          cursor: isUploading ? "wait" : "pointer",
        }}
        {...getRootProps({
          role: "button",
          "aria-label": t("profileImageChangeText"),
          "aria-busy": isUploading,
          "aria-disabled": isUploading,
        })}
      >
        {profileImg && failedImage !== profileImg ? (
          <img
            key={profileImg}
            src={profileImg}
            alt="Profile"
            style={{ height: "100%", width: "100%", objectFit: "cover" }}
            onError={() => setFailedImage(profileImg)}
          />
        ) : (
          <div>
            <BsPersonAdd style={{ height: "6rem", width: "6rem" }} />
          </div>
        )}
      </div>
    </>
  );
};
